import React, { useMemo } from 'react';
import Details from '../Form/Details';
import ProductDescription from '../Form/ProductDescription';
import ProductMedia from '../Form/Media';
import Pricing from '../Form/Pricing';
import ProductInventory from '../Form/Inventory';
import FormFields from '../Form/FormFeilds';
import ProductStatus from '../Form/Status';
import Categories from '../Form/Categories';
import Tags from '../Form/Tags';
import Variant from '../Form/Variant';
import { ProductTypeSelector } from '../Form/ProductTypeSelector';
import DigitalFields from '../Form/DigitalFields';
import PhysicalFields from '../Form/PhysicalFields';
import ServicesFields from '../Form/ServicesFields';
import { useFormContext } from 'react-hook-form';

// Product type constants - internal to products module
const PRODUCT_TYPES = {
  DIGITAL: 'digital',
  PHYSICAL: 'physical',
  SERVICES: 'services',
} as const;

interface ProductFormProps {
  onPendingVariantsChange?: (variants: any[]) => void;
}

export default function ProductForm({
  onPendingVariantsChange,
}: ProductFormProps) {
  const { watch } = useFormContext();
  const variantMode = watch('variant_mode') || 'single'; // Default to single product
  const productType = watch('product_type');

  // Memoize conditional components to prevent unnecessary re-renders
  const productTypeFields = useMemo(() => {
    switch (productType) {
      case PRODUCT_TYPES.DIGITAL:
        return <DigitalFields />;
      case PRODUCT_TYPES.PHYSICAL:
        return <PhysicalFields />;
      case PRODUCT_TYPES.SERVICES:
        return <ServicesFields />;
      default:
        return null;
    }
  }, [productType]);

  const singleProductSections = useMemo(() => {
    if (variantMode === 'single') {
      return (
        <>
          <ProductDescription />
          <ProductMedia />
          <Pricing />
        </>
      );
    }
    return null;
  }, [variantMode]);

  const variantSection = useMemo(() => {
    if (variantMode === 'variant') {
      return (
        <Variant
          onPendingVariantsChange={onPendingVariantsChange}
          isNewProduct={true}
        />
      );
    }
    return null;
  }, [variantMode, onPendingVariantsChange]);

  return (
    <div className="p-4 lg:p-6">
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="xl:col-span-3 space-y-6">
          {/* Product Category Tabs - At top without block */}
          <ProductTypeSelector />

          <Details />

          {/* Show variant section only when variant_mode is 'variant' */}
          {variantSection}

          {/* Product Type Specific Fields */}
          {productTypeFields}

          {/* Show single product sections only when variant_mode is 'single' */}
          {singleProductSections}

          <ProductInventory />
          <FormFields />
        </div>

        {/* Sidebar */}
        <div className="xl:col-span-1">
          <div className="xl:sticky xl:top-6 space-y-4">
            <ProductStatus />
            <div className="space-y-4">
              <Categories />
              <Tags />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
